import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";
import { ToastContainer } from "react-toastify";
import { Paper, useTheme } from "@mui/material";
import { fetchUser } from "../../api/user";
import {
  getAuthToken,
  setAuthToken,
  extractTokenFromHash,
  extractTokenFromQuery,
} from "../../utils/authToken";
import {
  setTimezone,
  setAuthResolved,
  getStatusAction,
} from "../../store/reducers/app";
import { useContestId } from "../../hooks/useContestId";
import config from "../../config";
import RootModal from "../RootModal/RootModal";
import Router from "../Router";

const App = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const modalType = useSelector((state) => state.modal?.modalType);
  const modalProps = useSelector((state) => state.modal?.modalProps);

  const [searchParams, setSearchParams] = useSearchParams();
  const contestId = useContestId();
  const [userStatus, setUserStatus] = useState();
  const [contestStatus, setContestStatus] = useState();

  const initializeSession = useCallback(
    async (spotlightId, userId) => {
      const _status = await dispatch(
        getStatusAction({
          spotlightId: spotlightId,
          userId: userId,
          contestId: contestId,
        }),
      );
      const status = _status?.payload;

      if (!!status) {
        setUserStatus({
          entryPage: status?.user_status?.entry_page,
          focus: status?.user_status?.focus,
        });
        setContestStatus({
          playStartsAt: status?.contest_status?.play_starts_at,
          currentStage: status?.contest_status?.current_stage,
        });
      }
    },
    [contestId, dispatch],
  );

  useEffect(() => {
    const getInitialProps = async () => {
      // DEV ONLY (config.allowDevLogin): a ?dev_user_id=<n> param logs straight
      // in as that user id by calling /api/status directly, skipping the
      // Spotlight /users/me call entirely. A distinct param name (not plain
      // user_id) so it never collides with the user_id that rides along in real
      // Lobby URLs. Never active in staging/production (allowDevLogin unset).
      const devUserId = config.allowDevLogin
        ? searchParams.get("dev_user_id")
        : null;
      if (devUserId && !userStatus) {
        initializeSession(undefined, devUserId);
        dispatch(setTimezone());
        return;
      }

      // Capture a JWT delivered in the URL fragment by the Lobby, store the
      // bare token, then strip it from the URL so it doesn't linger in history.
      const hashToken = extractTokenFromHash(window.location.hash);
      if (hashToken) {
        setAuthToken(hashToken, true);
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );
      } else {
        // Fallback: the Lobby may instead deliver the JWT as a `tref` query
        // param. Only checked when there's no hash token, so the hash always
        // takes priority if both are somehow present. Strip `tref` from the
        // URL afterward the same way the hash token is stripped, so it
        // doesn't linger in history or get re-processed on re-render.
        const trefToken = extractTokenFromQuery(searchParams.toString());
        if (trefToken) {
          setAuthToken(trefToken, true);
          const next = new URLSearchParams(searchParams);
          next.delete("tref");
          setSearchParams(next, { replace: true });
        }
      }

      const jwt = getAuthToken();

      if (!!jwt && !userStatus) {
        await fetchUser(dispatch)
          .then((res) => {
            const spotlightUser = res?.data;
            if (spotlightUser) {
              initializeSession(spotlightUser.id);
            } else {
              dispatch(setAuthResolved());
            }
          })
          .catch((err) => {
            console.error("Error in Spotlight API call [fetchUser]: ", err);
            dispatch(setAuthResolved());
          });
      } else if (!jwt) {
        // No token anywhere -> clear the global spinner so the sign-in
        // prompt renders instead of hanging on "Loading…".
        dispatch(setAuthResolved());
      }

      dispatch(setTimezone());
    };

    getInitialProps();
  }, [dispatch, userStatus, initializeSession, searchParams]);

  // Keep ?contest_id pinned in the URL so a contest can't silently flip. In-app
  // navigation (tab switches, the group->knockout redirect, etc.) can drop the
  // query param; the moment it's gone, anything reading the raw param would fall
  // back to the default contest. useContestId still resolves the locked id from
  // localStorage, so here we simply re-attach it to the URL (replace, no history
  // entry), keeping every reader on the same contest and the link shareable.
  useEffect(() => {
    const raw = searchParams.get("contest_id");
    if (
      (raw == null || raw === "") &&
      Number.isFinite(contestId) &&
      contestId > 0
    ) {
      const next = new URLSearchParams(searchParams);
      next.set("contest_id", String(contestId));
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, contestId]);

  return (
    <Paper
      sx={[
        {
          width: "100%",
          height: "100%",
          backgroundColor: theme.branding.gray[50],
          justifyContent: "center",
        },
        (theme) =>
          theme.applyStyles("dark", {
            backgroundColor: theme.branding.darkBlue[900],
          }),
      ]}
      elevation={0}
    >
      <RootModal modalType={modalType} modalProps={modalProps} />
      <Router userStatus={userStatus} contestStatus={contestStatus} />
      <ToastContainer />
    </Paper>
  );
};

export default App;
